import React, { useEffect, Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';
import { Button, H1, Div, F } from 'components/Ui';
import Loader from 'components/Loader';
import { isPastDeliveryCutOffDateTime, getProductName } from 'utils/common';
import { frequencyTitles, GTM_TRACKING_TAGS } from 'utils/constants';
import classNames from 'classnames';
import { sortBy } from 'lodash';
import {
  selectRecurringOrder,
  selectRecurringDataIsLoading,
  selectSelectedServiceLocation,
  selectSelectedServiceLocationId,
  selectServiceLocationEquipment,
  selectSelectedRecurringDeliveryId,
  selectIsFirstDelivery,
  selectIsSelectedServiceLocationExcluded,
} from 'containers/PrimoAccount/selectors';
import {
  selectIsHardLockout,
  selectIsEquipmentPickupLockout,
} from 'containers/PrimoProfile/selectors';
import { createStructuredSelector } from 'reselect';
import {
  getServiceLocationRecurringDeliveries as getRecurringOrderRequest,
  loadRecurringOrder as loadRecurringOrderRequest,
  getServiceLocationEquipment as getServiceLocationEquipmentRequest,
  showSkipDialog as showSkipDialogRequest,
} from 'containers/PrimoAccount/actions';
import { useIsVertPhone } from 'utils/hooks';
import { useTheme } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
import { titleCase } from 'utils/string';
import OrderEditor from '../../PrimoAccount/OrderEditor';
import EditDeliveryOrderDrawer from '../../PrimoAccount/FutureOrders/EditDeliveryOrderDrawer';
import ProcessingModal from '../../PrimoAccount/FutureOrders/ProcessingModal';
import {
  loadDeliveryOrder as loadDeliveryOrderRequest,
  removeHold as removeHoldRequest,
  loadCalendarDates as loadCalendarDatesRequest,
} from '../../PrimoAccount/actions';
import { selectCalendarDates } from '../../PrimoAccount/selectors';
import GenericBlock from '../GenericBlock';
import styles from './styles.scss';
import commonStyles from '../styles.scss';

const getTypeText = (order) => {
  const type = order.skipDelivery ? 'skip' : order.type;
  switch (type) {
    case 'oneTime':
      return 'Delivery Scheduled';
    case 'recurring':
      return 'Delivery Scheduled';
    case 'skip':
      return 'Delivery Skipped';
    default:
      return null;
  }
};

const getStyle = (order, isVertPhone) => {
  const type = order.skipDelivery ? 'skip' : 'scheduled';
  let style = null;
  switch (type) {
    case 'scheduled':
      style = styles.scheduledDelivery;
      break;
    case 'skip':
      style = styles.skippedDelivery;
      break;
    default:
      return null;
  }
  return classNames(style, {
    [styles.roundVPhone]: isVertPhone,
  });
};

const sortedOrders = (calendarDates, isVertPhone) => {
  const orders = [];
  const calendarKeys = Object.keys(calendarDates);
  calendarKeys.forEach((yearMonthKey) => {
    const dates = calendarDates[yearMonthKey];
    const dateKeys = Object.keys(dates);
    dateKeys.forEach((dayKey) => {
      const order = dates[dayKey];
      const text = getTypeText(order);
      if (text) {
        const date = `${yearMonthKey}-${dayKey}`;
        const day = moment(date).format('MM/DD/YY');
        const style = getStyle(order, isVertPhone);
        orders.push({ ...order, date, day, text, style });
      }
    });
  });
  return sortBy(orders, (order) => moment(order.date).format('YYYYMMDD'));
};

const RecurringOrder = ({
  serviceLocation,
  getServiceLocationRecurringDeliveries,
  loadRecurringOrder,
  recurringOrder,
  equipments,
  calendarDates,
  recurringDeliveryId,
  dataIsLoading,
  getServiceLocationEquipment,
  showSkipDialog,
  isHardLockout,
  isFirstDelivery,
  loadDeliveryOrder,
  removeHold,
  loadCalendarDates,
  isEquipmentPickupLockout,
  isSelectedServiceLocationExcluded,
}) => {
  useEffect(() => {
    if (recurringDeliveryId) {
      getServiceLocationRecurringDeliveries();
      getServiceLocationEquipment();
    }
  }, [recurringDeliveryId]);

  const isVertPhone = useIsVertPhone();
  const [deliveryOrderToLoad, changeDeliveryOrderToLoad] = useState();
  const onEdit = () => {
    loadRecurringOrder();
  };

  useEffect(() => {
    if (deliveryOrderToLoad) {
      const found = sortedOrders(calendarDates, isVertPhone).find(
        (cd) => cd.date === deliveryOrderToLoad.date,
      );
      if (found && !found.skipDelivery) {
        loadDeliveryOrder(found);
        changeDeliveryOrderToLoad(null);
      }
    }
  }, [calendarDates]);
  const handleShowSkip = () => showSkipDialog();
  const renderProduct = (product) => {
    const nameDesc = titleCase(getProductName(product));
    return (
      <div key={product.itemNumber}>
        {`${product.quantity} x ${nameDesc}, ${product.count}`}
      </div>
    );
  };
  const formatDate = (date) => moment(date).format('MMM DD YYYY');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isPastCutOff = isPastDeliveryCutOffDateTime(serviceLocation);
  const handleRefresh = () => {
    loadDates();
  };
  const deliveryOrder = {
    date: serviceLocation && serviceLocation.nextDeliveryDate,
    cutOffDateTime: moment(
      serviceLocation && serviceLocation.nextDeliveryDate,
    ).subtract(1, 'days'),
  };
  const removeHoldSuccessCallback = () => {
    changeDeliveryOrderToLoad(deliveryOrder);
    loadDates();
  };
  const onEditDeliveryOrder = () => {
    localStorage.setItem('profilePage', 'true');
    localStorage.removeItem('nextDeliveryPage');
    loadDeliveryOrder(deliveryOrder);
  };

  const handleRemoveHold = () => {
    removeHold({ deliveryOrder, successCallback: removeHoldSuccessCallback });
  };
  const loadDates = () => {
    if (serviceLocation) {
      const fromDate = moment().format('YYYY-MM-DD');
      const toDate = moment().add(90, 'days').format('YYYY-MM-DD');

      loadCalendarDates({
        serviceLocationId: serviceLocation.id,
        toDate,
        fromDate,
      });
    }
  };

  const enableEdit = !(
    isFirstDelivery ||
    isSelectedServiceLocationExcluded ||
    isEquipmentPickupLockout ||
    isHardLockout
  );

  const deliveryFrequency = serviceLocation
    ? frequencyTitles[serviceLocation.deliveryFrequency]
    : '';

  return (
    <GenericBlock title="Order Profile">
      <div
        className={commonStyles.container}
        data-tracking={GTM_TRACKING_TAGS['Order Profile']}
      >
        <div className={commonStyles.blockContainer}>
          {!isMobile && (
            <div className={commonStyles.blockHeaderContainer}>
              <H1 className={commonStyles.blockHeaderText}>Order Profile</H1>
            </div>
          )}
          <Loader loading={dataIsLoading} local>
            <div className={commonStyles.blockContent}>
              <div
                className={classNames(commonStyles.section, {
                  [styles.thinSection]:
                    !recurringOrder || !recurringOrder.length,
                })}
              >
                <div
                  className={classNames(
                    commonStyles.subBlock,
                    styles.subBlockOverRide,
                  )}
                >
                  <Div className={commonStyles.subTitle}>Recurring Order</Div>
                  {recurringOrder && recurringOrder.length ? (
                    <div className={commonStyles.subBlockContent}>
                      {recurringOrder.map((item) => renderProduct(item))}
                      {enableEdit && (
                        <Button
                          link
                          onClick={onEdit}
                          text="Edit recurring order"
                          className={styles.button}
                        />
                      )}
                    </div>
                  ) : (
                    <Div>You currently have no recurring order.</Div>
                  )}
                </div>
                <div
                  className={classNames(
                    commonStyles.subBlock,
                    styles.subBlockOverRide,
                  )}
                >
                  <Div className={commonStyles.subTitle}>Your Equipment</Div>
                  <div className={commonStyles.subBlockContent}>
                    {equipments && equipments.length ? (
                      equipments.map((equipment, i) => (
                        <div key={i}>{titleCase(equipment.description)}</div>
                      ))
                    ) : (
                      <Div>You currently have no equipment</Div>
                    )}
                  </div>
                </div>
              </div>
              {!isMobile && (
                <div className={commonStyles.section}>
                  <div className={commonStyles.subBlock}>
                    <div className={commonStyles.subTitle} />
                    <div className={commonStyles.subBlockContent} />
                  </div>
                </div>
              )}
              {Boolean(recurringOrder && recurringOrder.length) && (
                <Fragment>
                  <div className={commonStyles.section}>
                    <div className={commonStyles.subBlock}>
                      <Div className={commonStyles.subTitle}>
                        Next Delivery Date
                      </Div>
                      <div className={commonStyles.subBlockContent}>
                        {serviceLocation &&
                          serviceLocation.nextDeliveryDate && (
                            <div>
                              {formatDate(serviceLocation.nextDeliveryDate)}
                            </div>
                          )}
                        {enableEdit && !isPastCutOff && (
                          <div>
                            <Button
                              link
                              text="Edit next delivery"
                              onClick={() => onEditDeliveryOrder()}
                              className={styles.button}
                            />
                            <Button
                              link
                              onClick={handleShowSkip}
                              text="Skip next delivery"
                              className={styles.button}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={commonStyles.subBlock}>
                      <Div className={commonStyles.subTitle}>
                        Delivery Frequency
                      </Div>
                      <div className={commonStyles.subBlockContent}>
                        <Loader
                          loading={dataIsLoading || !serviceLocation}
                          local
                        >
                          <div>
                            <F>{'Your delivery is'}</F>{' '}
                            <F>{deliveryFrequency}</F>
                          </div>
                        </Loader>
                      </div>
                    </div>
                    <div className={commonStyles.subBlock}>
                      <Div className={commonStyles.subTitle}>
                        Your Current Inventory
                      </Div>
                      <div className={commonStyles.subBlockContent}>
                        <div>
                          <F>It looks like you currently have</F>{' '}
                          {serviceLocation.bottlesOnHand} <F>bottles.</F>
                        </div>
                        <Div className={styles.note}>
                          Not right? Call 866-407-7873
                        </Div>
                      </div>
                    </div>
                  </div>
                </Fragment>
              )}
            </div>
          </Loader>
        </div>
      </div>
      <OrderEditor />
      <EditDeliveryOrderDrawer
        onRefresh={handleRefresh}
        onRemoveHold={handleRemoveHold}
      />
      <ProcessingModal />
    </GenericBlock>
  );
};

RecurringOrder.propTypes = {
  recurringOrder: PropTypes.array,
  equipments: PropTypes.array,
  dataIsLoading: PropTypes.bool,
  loadRecurringOrder: PropTypes.func.isRequired,
  getServiceLocationEquipment: PropTypes.func.isRequired,
  showSkipDialog: PropTypes.func.isRequired,
  getServiceLocationRecurringDeliveries: PropTypes.func.isRequired,
  loadDeliveryOrder: PropTypes.func.isRequired,
  calendarDates: PropTypes.object.isRequired,
  recurringDeliveryId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  serviceLocation: PropTypes.object,
  isHardLockout: PropTypes.bool.isRequired,
  isFirstDelivery: PropTypes.bool.isRequired,
  isEquipmentPickupLockout: PropTypes.bool.isRequired,
  isSelectedServiceLocationExcluded: PropTypes.bool.isRequired,
};

RecurringOrder.defaultProps = {
  recurringOrder: [],
};

const mapStateToProps = createStructuredSelector({
  calendarDates: selectCalendarDates(),
  dataIsLoading: selectRecurringDataIsLoading(),
  recurringOrder: selectRecurringOrder(),
  recurringDeliveryId: selectSelectedRecurringDeliveryId(),
  serviceLocation: selectSelectedServiceLocation(),
  serviceLocationId: selectSelectedServiceLocationId(),
  equipments: selectServiceLocationEquipment(),
  isHardLockout: selectIsHardLockout(),
  isFirstDelivery: selectIsFirstDelivery(),
  isEquipmentPickupLockout: selectIsEquipmentPickupLockout(),
  isSelectedServiceLocationExcluded: selectIsSelectedServiceLocationExcluded(),
});

const mapDispatchToProps = {
  loadRecurringOrder: loadRecurringOrderRequest,
  getServiceLocationRecurringDeliveries: getRecurringOrderRequest,
  getServiceLocationEquipment: getServiceLocationEquipmentRequest,
  showSkipDialog: showSkipDialogRequest,
  loadCalendarDates: loadCalendarDatesRequest,
  loadDeliveryOrder: loadDeliveryOrderRequest,
  removeHold: removeHoldRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(RecurringOrder);
